<template>
	<div class="project-profit-headings">
		<SuiDataTable :col-definition="colDefinition" :loading="loading">
			<SuiDataTableRow>
				<SuiDataTableCell class="project-profit-headings__regular-row">
					{{ $t('revenue') }}
				</SuiDataTableCell>
			</SuiDataTableRow>
			<SuiDataTableRow v-if="canSeeMarginAndMarkup">
				<SuiDataTableCell class="project-profit-headings__regular-row">
					{{ $t('cost') }}
				</SuiDataTableCell>
			</SuiDataTableRow>
			<SuiDataTableRow v-if="canSeeCostBreakdown">
				<SuiDataTableCell
					class="project-profit-headings__narrow-row project-profit-headings--shadow"
				>
					<span class="project-profit-headings__sub-title">
						{{ $t('labor_cost') }}
					</span>
				</SuiDataTableCell>
			</SuiDataTableRow>
			<SuiDataTableRow v-if="canSeeCostBreakdown">
				<SuiDataTableCell class="project-profit-headings__narrow-row">
					<span class="project-profit-headings__sub-title">
						{{ $t('external_cost') }}
					</span>
				</SuiDataTableCell>
			</SuiDataTableRow>
			<SuiDataTableRow v-if="canSeeProfit">
				<SuiDataTableCell class="project-profit-headings__total">
					<strong class="project-profit-headings__highlighted">
						{{ $t('project_profit') }}
					</strong>
					<span class="project-profit-headings__percentage">
						{{ $t('profit_margin') }}
					</span>
				</SuiDataTableCell>
			</SuiDataTableRow>
		</SuiDataTable>
	</div>
</template>

<script setup lang="ts">
import {
	SuiDataTable,
	SuiDataTableCell,
	SuiDataTableRow,
} from '@developers/scoro-ui';
import { Setting } from '@/constants/settings';
import { computed, toRefs } from 'vue';
import { useTranslation } from '@/composables';
import { useSettings } from '@/composables/settings/useSettings';
const { t } = useTranslation();
const { setting } = useSettings();

const props = defineProps<{
	canSeeMarginAndMarkup: boolean;
	canSeeCostBreakdown: boolean;
	canSeeProfit: boolean;
	loading: boolean;
}>();

const { canSeeMarginAndMarkup, canSeeCostBreakdown, canSeeProfit, loading } =
	toRefs(props);

const displayCurrency = computed(() => {
	const displayCurrency = setting(Setting.DISPLAY_CURRENCY);

	if (displayCurrency) {
		return `(${displayCurrency})`;
	}

	return '';
});

const colDefinition = computed(() => [
	{
		id: 'caption',
		title: `${t('table')} ${displayCurrency.value}`,
		width: '100%',
	},
]);
</script>

<style scoped lang="scss">
.project-profit-headings {
	--sui-data-table-header-cell-padding: 3.25rem 0 2.5rem 0;
	--sui-data-table-header-cell-color: #{$blue-50};
	--sui-data-table-header-cell-fw: #{$scoro-fw-medium};

	width: 20%;
	min-width: max-content;
	text-wrap: nowrap;

	:deep(.sui-data-table-header__col) {
		font-size: 1.75rem;
	}

	&__regular-row {
		line-height: 2.5rem;
		padding: 2rem 1.5rem;
		gap: 1rem;
		font-size: 1.625rem;
		font-weight: $scoro-fw-medium;
	}

	&--shadow {
		box-shadow: $scoro-box-shadow-line-upper-inside;
	}

	&__narrow-row {
		padding: 1.625rem 0;
		gap: 1.5rem;
		background-color: $neutral-10;
		color: $neutral-80;
		font-size: 1.5rem;
		font-weight: $scoro-fw-normal;
	}

	&__sub-title {
		padding-left: 2rem;
	}

	&__total {
		--sui-data-table-cell-padding: 2rem 1.5rem;

		display: flex;
		flex-direction: column;
	}

	&__highlighted {
		color: $neutral-90;
		font-size: 1.625rem;
	}

	&__percentage {
		color: $neutral-70;
		font-size: 1.5rem;
		font-weight: $scoro-fw-normal;
		line-height: 2.13rem;
	}
}
</style>
